import React, { useEffect, useState } from "react";
import { Table, Tooltip } from "flowbite-react";
import {
  HiInformationCircle,
  HiOutlineInformationCircle,
} from "react-icons/hi";
import { useForecastContext } from "../../contexts/ForecastContext";
import { formatCurrency } from "../../utils";
import { useCurrency } from "../../hooks";
import { useEmptyStateTracking } from "../../hooks/useEmptyStateTracking";
import { TooltipContent } from "../../components";

export const RevenueForecastSummary = ({
  selectedTimeRange,
  loading: parentLoading,
}) => {
  const [, setComponentKey] = useState(0);
  const { currencySymbol } = useCurrency();

  const {
    loading: summaryLoading,
    getTimeRangeData,
    transformForecastMetrics,
    transformRevenueDrivers,
  } = useForecastContext();

  // Force re-render when time range changes to ensure fresh data access
  useEffect(() => {
    setComponentKey((prev) => prev + 1);
  }, [selectedTimeRange]);

  // Get current data (should be cached)
  const currentData = getTimeRangeData(selectedTimeRange);

  const revenueMetrics = transformForecastMetrics(currentData);
  const revenueDrivers = transformRevenueDrivers(currentData);

  const formatPercentage = (value) => {
    // PAN-461: null means "there is no answer", not zero. A CSM whose accounts
    // have no renewals due this period has no retention figure — showing 0% or
    // 100% both state something untrue, and 100% is worse because nobody
    // questions good news.
    if (value === null || value === undefined) {
      return "—";
    }
    return `${value.toFixed(1)}%`;
  };

  // Show loading state if either parent or summary is loading
  const isLoading = parentLoading || summaryLoading;

  // Alarm-scoped surface: a mature team's forecast summary rendering "no
  // data" is a broken-pipeline signature, not an expected empty.
  useEmptyStateTracking("forecast_summary", {
    isEmpty: !currentData || !revenueMetrics || !revenueDrivers,
    isLoading,
    properties: { time_range: selectedTimeRange },
  });

  if (isLoading) {
    return (
      <>
        <div className="mt-1 flex w-full flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
          <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
            <div className="text-2xl font-bold leading-7 text-gray-800">
              Revenue Forecast (Closed + Open Forecast)
            </div>
            <HiOutlineInformationCircle className="mt-1 opacity-50" />
          </div>
          <div className="flex h-64 w-full items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading...</div>
          </div>
        </div>
        <div className="mt-4 flex w-full flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
          <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
            <div className="text-2xl font-bold leading-7 text-gray-800">
              Revenue Drivers & Retention (Closed + Open forecast)
            </div>
            <HiOutlineInformationCircle className="mt-1 opacity-50" />
          </div>
          <div className="flex h-64 w-full items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading...</div>
          </div>
        </div>
      </>
    );
  }

  // Show no data message if we don't have the required data
  // Changed condition to check for currentData existence first
  if (!currentData || !revenueMetrics || !revenueDrivers) {
    return (
      <>
        <div className="mt-1 flex w-full flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
          <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
            <div className="text-2xl font-bold leading-7 text-gray-800">
              Revenue Forecast (Closed + Open Forecast)
            </div>
            <HiOutlineInformationCircle className="mt-1 opacity-50" />
          </div>
          <div className="flex h-64 w-full items-center justify-center">
            <div className="text-gray-500">
              No data available for the selected time range
            </div>
          </div>
        </div>
        <div className="mt-1 flex w-full flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
          <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
            <div className="text-2xl font-bold leading-7 text-gray-800">
              Revenue Drivers & Retention (Closed + Open forecast)
            </div>
            <HiOutlineInformationCircle className="mt-1 opacity-50" />
          </div>
          <div className="flex h-64 w-full items-center justify-center">
            <div className="text-gray-500">
              No data available for the selected time range
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Revenue Forecast Metrics Table */}
      <div className="mt-1 flex w-full min-w-0 flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
        <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
          <div className="text-2xl font-bold leading-7 text-gray-800">
            Revenue Forecast (Closed + Open Forecast)
          </div>
          <Tooltip
            content={
              <TooltipContent
                title="About This Forecast"
                size="lg"
                text={
                  <div>
                    This forecast combines deals that have already been{" "}
                    <strong>Closed</strong> this period with{" "}
                    <strong>Open</strong> deals (like upcoming renewals) to
                    predict the most likely end-of-period revenue.
                  </div>
                }
              />
            }
            // eslint-disable-next-line react/style-prop-object
            style="light"
          >
            <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
          </Tooltip>
        </div>

        <div className="w-full overflow-x-auto">
          <Table
            striped
            hoverable
            className="w-full table-auto bg-white drop-shadow-none"
          >
            <Table.Head>
              <Table.HeadCell className="whitespace-nowrap px-6 py-3 text-left">
                Metric
              </Table.HeadCell>
              <Table.HeadCell className="whitespace-nowrap px-6 py-3 text-left">
                Value
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Start MRR (all contracts, period start)
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {revenueMetrics.startMrr}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center gap-2">
                    Net Δ MRR (all drivers)
                    <Tooltip
                      content={
                        <TooltipContent
                          title="About Feature & Account Expansion"
                          size="lg"
                          text="This is the net change in Monthly Recurring Revenue forecasted for the period. It is calculated by adding all growth (like Expansion and Upsells) and subtracting all revenue loss (like Contraction and Churn)."
                        />
                      }
                      // eslint-disable-next-line react/style-prop-object
                      style="light"
                    >
                      <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {revenueMetrics.netDeltaMrr}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  New Business MRR
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {revenueMetrics.newMrr}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Forecast End MRR
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {revenueMetrics.forecastEndMrr}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Forecast ARR (12× MRR)
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {revenueMetrics.forecastArr}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Forecast Δ ARR
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {revenueMetrics.forecastDeltaArr}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center gap-2">
                    Gross Revenue Retention (GRR)
                    <Tooltip
                      content={
                        <TooltipContent
                          title="About Gross Revenue Retention (GRR)"
                          size="xl"
                          text={
                            <div>
                              GRR measures your ability to retain revenue from
                              the cohort of contracts up for renewal in this
                              period, excluding any expansion or upsell.
                              <ul className="list-disc pl-5">
                                <li>
                                  <strong>Formula:</strong> (Renewal Base MRR −
                                  Churn − Contraction) / Renewal Base MRR
                                </li>
                                <li>
                                  <strong>Renewal Base MRR:</strong> the MRR of
                                  contracts due for renewal in this period (not
                                  team-wide Start MRR).
                                </li>
                              </ul>
                            </div>
                          }
                        />
                      }
                      // eslint-disable-next-line react/style-prop-object
                      style="light"
                    >
                      <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {formatPercentage(revenueMetrics.grossRevenueRetention)}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center gap-2">
                    Net Revenue Retention (NRR)
                    <Tooltip
                      content={
                        <TooltipContent
                          title="About Net Revenue Retention (NRR)"
                          size="xl"
                          text={
                            <div>
                              NRR measures your ability to retain and grow
                              revenue from the cohort of contracts up for
                              renewal in this period. Unlike GRR, this metric
                              includes expansion revenue from those accounts.
                              <ul className="list-disc pl-5">
                                <li>
                                  <strong>Formula:</strong> (Renewal Base MRR +
                                  Expansion − Churn − Contraction) / Renewal
                                  Base MRR
                                </li>
                                <li>
                                  <strong>Renewal Base MRR:</strong> the MRR of
                                  contracts due for renewal in this period (not
                                  team-wide Start MRR).
                                </li>
                              </ul>
                            </div>
                          }
                        />
                      }
                      // eslint-disable-next-line react/style-prop-object
                      style="light"
                    >
                      <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {formatPercentage(revenueMetrics.netRevenueRetention)}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center gap-2">
                    Logo Retention (forecast)
                    <Tooltip
                      content={
                        <TooltipContent
                          title="About Logo Retention"
                          size="xl"
                          text={
                            <div>
                              The percentage of the renewal cohort (contracts up
                              for renewal in this period) forecasted to be
                              retained, regardless of revenue size.
                              <ul className="list-disc pl-5">
                                <li>
                                  <strong>Formula:</strong> (Contracts Up for
                                  Renewal − Churned − Communicated Churn) /
                                  Contracts Up for Renewal
                                </li>
                                <li>
                                  Denominator matches the same renewal cohort
                                  used by Coverage above — not team-wide
                                  customer count.
                                </li>
                              </ul>
                            </div>
                          }
                        />
                      }
                      // eslint-disable-next-line react/style-prop-object
                      style="light"
                    >
                      <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {formatPercentage(revenueMetrics.logoRetention.percentage)}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center gap-2">
                    Coverage (closed so far)
                    <Tooltip
                      content={
                        <TooltipContent
                          title="About Coverage"
                          size="xl"
                          text={
                            <div>
                              This shows the percentage of renewal contracts
                              that have already been <strong>Closed</strong>{" "}
                              this period, compared to the total number of
                              contracts that were up for renewal. It measures
                              the progress of your renewal{" "}
                              <strong>volume</strong> (count of deals), not
                              their revenue value.
                            </div>
                          }
                        />
                      }
                      // eslint-disable-next-line react/style-prop-object
                      style="light"
                    >
                      <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 font-semibold">
                  {formatPercentage(revenueMetrics.coverage.percentage)}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      </div>

      {/* Revenue Drivers & Retention Table */}
      <div className="mt-4 flex w-full min-w-0 flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
        <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
          <div className="text-2xl font-bold leading-7 text-gray-800">
            Revenue Drivers & Retention (Closed + Open forecast)
          </div>
          <Tooltip
            content={
              <TooltipContent
                title="About Revenue Drivers"
                size="lg"
                text={
                  <div>
                    This table breaks down your total 'Net Δ MRR' (from the
                    module above) into its core components. It shows you exactly
                    where your revenue growth and losses are coming from (e.g.,
                    Expansion, Upsells, Contraction, or Churn).
                  </div>
                }
              />
            }
            // eslint-disable-next-line react/style-prop-object
            style="light"
          >
            <HiInformationCircle className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-600" />
          </Tooltip>
        </div>

        <div className="w-full overflow-x-auto">
          <Table
            striped
            hoverable
            className="table-aut w-full drop-shadow-none"
          >
            <Table.Head>
              <Table.HeadCell className="whitespace-nowrap px-6 py-3 text-left">
                Category
              </Table.HeadCell>
              <Table.HeadCell className="whitespace-nowrap px-6 py-3 text-right">
                Closed to Date
              </Table.HeadCell>
              <Table.HeadCell className="whitespace-nowrap px-6 py-3 text-right">
                Remaining Expected
              </Table.HeadCell>
              <Table.HeadCell className="whitespace-nowrap px-6 py-3 text-right">
                Total Expected
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Renewal Base
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.renewalBase.closed}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.renewalBase.remaining}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right font-semibold">
                  {revenueDrivers.renewalBase.total}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Expansion
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.expansion.closed}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.expansion.remaining}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right font-semibold">
                  {revenueDrivers.expansion.total}
                </Table.Cell>
              </Table.Row>

              <Table.Row className="bg-gray-25">
                <Table.Cell className="whitespace-nowrap px-6 py-2 pl-12 text-gray-500">
                  &gt; Upsell / upgrade
                </Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
              </Table.Row>

              <Table.Row className="bg-gray-25">
                <Table.Cell className="whitespace-nowrap px-6 py-2 pl-12 text-gray-500">
                  &gt; Expansion at renewal (+)
                </Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Contraction
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.contraction.closed}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.contraction.remaining}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right font-semibold">
                  {revenueDrivers.contraction.total}
                </Table.Cell>
              </Table.Row>

              <Table.Row>
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Churn
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.churn.closed}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {revenueDrivers.churn.remaining}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right font-semibold">
                  {revenueDrivers.churn.total}
                </Table.Cell>
              </Table.Row>

              {revenueDrivers.newBusiness?.total && (
                <Table.Row>
                  <Table.Cell className="whitespace-nowrap px-6 py-4">
                    New Business
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                    {revenueDrivers.newBusiness.closed}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                    {revenueDrivers.newBusiness.remaining}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap px-6 py-4 text-right font-semibold">
                    {revenueDrivers.newBusiness.total}
                  </Table.Cell>
                </Table.Row>
              )}

              {/* Summary row: existing-book net movement */}
              <Table.Row className="border-t-2 border-gray-300 bg-gray-50 font-bold text-black">
                <Table.Cell className="whitespace-nowrap px-6 py-4">
                  Net Δ MRR (existing book)
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {formatCurrency(revenueDrivers.allClosed, currencySymbol)}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {formatCurrency(revenueDrivers.allRemaining, currencySymbol)}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-6 py-4 text-right">
                  {formatCurrency(revenueDrivers.allTotal, currencySymbol)}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      </div>
    </>
  );
};
